import { useState } from 'react';
import { useBulkBroadcastActionsPermission } from 'src/components/common/BulkActions/BulkBroadcastActions/useBulkBroadcastActionsPermission';
import { useBulkActionsState } from 'src/components/common/BulkActions/useBulkActionsState';
import { useInventoryEventBulkActionsState } from 'src/components/Events/EventListing/InventoryEventListing/BulkActions/InventoryEventBulkActions/useInventoryEventBulkActionsState';
import { useBulkAddHoldAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkAddHoldAction';
import { useBulkEditBroadcastSettingsAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkEditBroadcastSettingsAction/useBulkEditBroadcastSettingsAction';
import { useBulkMergeSelectedListingsAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkMergeSelectedListingsAction';
import { useBulkRemoveHoldAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkRemoveHoldAction';
import { useBulkResetMergedListingsAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkResetMergedListingsAction';
import { useBulkSplitSelectedListingsAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkSplitSelectedListingsAction';
import { Content } from 'src/contexts/ContentContext';
import { PosDropdown } from 'src/core/POS/PosDropdown';
import { vars } from 'src/core/themes';
import { Button } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ChevronDownIcon, IconsFill } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { EventWithData, Feature } from 'src/WebApiController';

export interface BroadcastActionsProps {
  event: EventWithData['event'];
  isEventPage: boolean;
  listingCount: number;
}

export const BroadcastActions = ({ event }: BroadcastActionsProps) => {
  const { areBulkActionsEnabled } = useBulkActionsState();
  const [isLoading, setIsLoading] = useState(false);
  const { affectedVisibleListingIds, filterQueryWithEventIds } =
    useInventoryEventBulkActionsState(event);

  const { hasBroadcastPermission } = useBulkBroadcastActionsPermission();
  const hasBulkSplitListingsFeature = useUserHasFeature(
    Feature.BulkSplitListings
  );

  const bulkAddHold = useBulkAddHoldAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );

  const bulkRemoveHold = useBulkRemoveHoldAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );

  const bulkEditBroadcastSettings = useBulkEditBroadcastSettingsAction({
    affectedVisibleListingIds,
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event,
  });

  const bulkSplitToOriginalAction = useBulkResetMergedListingsAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );

  const bulkMergeSelectedListingsAction = useBulkMergeSelectedListingsAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );
  const bulkSplitSelectedListingsAction = useBulkSplitSelectedListingsAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );

  return (
    <>
      <PosDropdown
        key="event-broadcast-actions-dropdown"
        trigger={
          <Button variant="outline">
            <Content id={ContentId.Broadcast} />
            <ChevronDownIcon
              size={vars.iconSize.xs}
              fill={IconsFill.currentColor}
            />
          </Button>
        }
        align="start"
        triggerProps={{ disabled: isLoading || !areBulkActionsEnabled }}
      >
        {hasBroadcastPermission && bulkEditBroadcastSettings.dropDown}
        {bulkAddHold.dropDown}
        {bulkRemoveHold.dropDown}
        {bulkSplitToOriginalAction.dropDown}
        {bulkMergeSelectedListingsAction.dropDown}
        {hasBulkSplitListingsFeature &&
          bulkSplitSelectedListingsAction.dropDown}
      </PosDropdown>
    </>
  );
};
